"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var vector_1 = require("./vector");
var WGS84_POLAR_RADIUS = 6356752.3142451793;
var WGS84_EQUATORIAL_RADIUS = 6378137;
var WGS84_ECCENTRICITY_2 = 0.00669437999014;
exports.oneOverRadii = new vector_1.Vec3(1.0 / WGS84_EQUATORIAL_RADIUS, 1.0 / WGS84_EQUATORIAL_RADIUS, 1.0 / WGS84_POLAR_RADIUS);
exports.oneOverRadiiSquared = vector_1.Vec3.multiply(exports.oneOverRadii, exports.oneOverRadii, new vector_1.Vec3());
var centerToleranceSquared = 0.1;
var scaleToGeodeticSurfaceIntersection = new vector_1.Vec3();
var scaleToGeodeticSurfaceGradient = new vector_1.Vec3();
/***************************************************************************
 * A WGS84 Ellipsoid to approximate the Earth. Functions to convert between
 * coordinate systems, and various ellipsoidal math functions
 **************************************************************************/
function d2r(degs) {
    return degs * (Math.PI / 180);
}
exports.d2r = d2r;
function dist(p1, p2) {
    return Math.sqrt((p1.x - p2.x) * (p1.x - p2.x) + (p1.y - p2.y) * (p1.y - p2.y) + (p1.z - p2.z) * (p1.z - p2.z));
}
exports.dist = dist;
// TODO: [BM 08/03/2018] Add inverse.
function cartographicToCartesian(lat, lon, alt, result) {
    if (result === void 0) { result = new vector_1.Vec3(); }
    var v = WGS84_EQUATORIAL_RADIUS / Math.sqrt(1 - WGS84_ECCENTRICITY_2 * Math.sin(lat) * Math.sin(lat));
    var coslat = Math.cos(lat);
    result.x = (v + alt) * coslat * Math.cos(lon);
    result.y = (v + alt) * coslat * Math.sin(lon);
    result.z = ((1 - WGS84_ECCENTRICITY_2) * v + alt) * Math.sin(lat);
    return result;
}
exports.cartographicToCartesian = cartographicToCartesian;
function cartesianToCartographic(cartesian, result) {
    if (result === void 0) { result = { longitude: 0.0, latitude: 0.0, height: 0.0 }; }
    var p = scaleToGeodeticSurface(cartesian, new vector_1.Vec3());
    var n = geodeticSurfaceNormal(p, new vector_1.Vec3());
    var h = vector_1.Vec3.subtract(cartesian, p, new vector_1.Vec3());
    result.latitude = Math.asin(n.z);
    result.longitude = Math.atan2(n.y, n.x);
    result.height = Math.sign(vector_1.Vec3.dot(h, cartesian)) * h.length();
    return result;
}
exports.cartesianToCartographic = cartesianToCartographic;
/**
 * Scales the provided Cartesian position along the geodetic surface normal
 * so that it is on the surface of this ellipsoid.  If the position is
 * at the center of the ellipsoid, this function returns undefined.
 */
function scaleToGeodeticSurface(cartesian, result) {
    if (result === void 0) { result = new vector_1.Vec3(); }
    var positionX = cartesian.x;
    var positionY = cartesian.y;
    var positionZ = cartesian.z;
    var oneOverRadiiX = exports.oneOverRadii.x;
    var oneOverRadiiY = exports.oneOverRadii.y;
    var oneOverRadiiZ = exports.oneOverRadii.z;
    var x2 = positionX * positionX * oneOverRadiiX * oneOverRadiiX;
    var y2 = positionY * positionY * oneOverRadiiY * oneOverRadiiY;
    var z2 = positionZ * positionZ * oneOverRadiiZ * oneOverRadiiZ;
    // Compute the squared ellipsoid norm.
    var squaredNorm = x2 + y2 + z2;
    var ratio = Math.sqrt(1.0 / squaredNorm);
    // As an initial approximation, assume that the radial intersection is the projection point.
    var intersection = vector_1.Vec3.multiply(cartesian, ratio, scaleToGeodeticSurfaceIntersection);
    // If the position is near the center, the iteration will not converge.
    if (squaredNorm < centerToleranceSquared) {
        if (isFinite(ratio)) {
            result.copy(intersection);
        }
        return result;
    }
    var oneOverRadiiSquaredX = exports.oneOverRadiiSquared.x;
    var oneOverRadiiSquaredY = exports.oneOverRadiiSquared.y;
    var oneOverRadiiSquaredZ = exports.oneOverRadiiSquared.z;
    // Use the gradient at the intersection point in place of the true unit normal.
    // The difference in magnitude will be absorbed in the multiplier.
    var gradient = scaleToGeodeticSurfaceGradient;
    gradient.x = intersection.x * oneOverRadiiSquaredX * 2.0;
    gradient.y = intersection.y * oneOverRadiiSquaredY * 2.0;
    gradient.z = intersection.z * oneOverRadiiSquaredZ * 2.0;
    // Compute the initial guess at the normal vector multiplier, lambda.
    var lambda = (1.0 - ratio) * cartesian.length() / (0.5 * gradient.length());
    var correction = 0.0;
    var func;
    var denominator;
    var xMultiplier;
    var yMultiplier;
    var zMultiplier;
    var xMultiplier2;
    var yMultiplier2;
    var zMultiplier2;
    var xMultiplier3;
    var yMultiplier3;
    var zMultiplier3;
    do {
        lambda -= correction;
        xMultiplier = 1.0 / (1.0 + lambda * oneOverRadiiSquaredX);
        yMultiplier = 1.0 / (1.0 + lambda * oneOverRadiiSquaredY);
        zMultiplier = 1.0 / (1.0 + lambda * oneOverRadiiSquaredZ);
        xMultiplier2 = xMultiplier * xMultiplier;
        yMultiplier2 = yMultiplier * yMultiplier;
        zMultiplier2 = zMultiplier * zMultiplier;
        xMultiplier3 = xMultiplier2 * xMultiplier;
        yMultiplier3 = yMultiplier2 * yMultiplier;
        zMultiplier3 = zMultiplier2 * zMultiplier;
        func = x2 * xMultiplier2 + y2 * yMultiplier2 + z2 * zMultiplier2 - 1.0;
        // "denominator" here refers to the use of this expression in the velocity and acceleration
        // computations in the sections to follow.
        denominator = x2 * xMultiplier3 * oneOverRadiiSquaredX + y2 * yMultiplier3 * oneOverRadiiSquaredY +
            z2 * zMultiplier3 * oneOverRadiiSquaredZ;
        var derivative = -2.0 * denominator;
        correction = func / derivative;
    } while (Math.abs(func) > 0.01);
    result.x = positionX * xMultiplier;
    result.y = positionY * yMultiplier;
    result.z = positionZ * zMultiplier;
    return result;
}
/**
 * Computes the normal of the plane tangent to the surface of the ellipsoid at the provided position.
 */
function geodeticSurfaceNormal(cartesian, result) {
    if (result === void 0) { result = new vector_1.Vec3(); }
    result = vector_1.Vec3.multiply(cartesian, exports.oneOverRadiiSquared, result);
    return result.normalize();
}
exports.geodeticSurfaceNormal = geodeticSurfaceNormal;
function radiusAtLatitude(latitude) {
    var t = Math.cos(latitude);
    return (1.0 - t) * WGS84_EQUATORIAL_RADIUS + t * WGS84_POLAR_RADIUS;
}
exports.radiusAtLatitude = radiusAtLatitude;
/**
 * Computes a 4x4 transformation matrix from an east-north-up reference frame
 * centered at the provided origin to the provided ellipsoid's world space.
 * X, Y, and Z point in the East, North, and Up directions.
 */
var scratchEast = new vector_1.Vec3();
var scratchNorth = new vector_1.Vec3();
var scratchUp = new vector_1.Vec3();
function eastNorthUpToWorld(origin, result, inverseResult) {
    // If x and y are zero, assume origin is at a pole, which is a special case.
    if (equals0(origin.x, EPSILON14) && equals0(origin.y, EPSILON14)) {
        var sign = Math.sign(origin.z);
        scratchEast.init(0, 1, 0);
        scratchNorth.init(-1 * sign, 0, 0);
        scratchUp.init(0, 0, 1 * sign);
    }
    else {
        geodeticSurfaceNormal(origin, scratchUp);
        scratchEast.x = -origin.y;
        scratchEast.y = origin.x;
        scratchEast.z = 0.0;
        scratchEast.normalize();
        vector_1.Vec3.cross(scratchUp, scratchEast, scratchNorth);
    }
    result.m[0] = scratchEast.x;
    result.m[1] = scratchEast.y;
    result.m[2] = scratchEast.z;
    result.m[3] = 0.0;
    result.m[4] = scratchNorth.x;
    result.m[5] = scratchNorth.y;
    result.m[6] = scratchNorth.z;
    result.m[7] = 0.0;
    result.m[8] = scratchUp.x;
    result.m[9] = scratchUp.y;
    result.m[10] = scratchUp.z;
    result.m[11] = 0.0;
    result.m[12] = origin.x;
    result.m[13] = origin.y;
    result.m[14] = origin.z;
    result.m[15] = 1.0;
    if (inverseResult) {
        inverseResult.m[0] = scratchEast.x;
        inverseResult.m[1] = scratchNorth.x;
        inverseResult.m[2] = scratchUp.x;
        inverseResult.m[3] = 0.0;
        inverseResult.m[4] = scratchEast.y;
        inverseResult.m[5] = scratchNorth.y;
        inverseResult.m[6] = scratchUp.y;
        inverseResult.m[7] = 0.0;
        inverseResult.m[8] = scratchEast.z;
        inverseResult.m[9] = scratchNorth.z;
        inverseResult.m[10] = scratchUp.z;
        inverseResult.m[11] = 0.0;
        inverseResult.m[12] = -origin.dot(scratchEast);
        inverseResult.m[13] = -origin.dot(scratchNorth);
        inverseResult.m[14] = -origin.dot(scratchUp);
        inverseResult.m[15] = 1.0;
    }
    return result;
}
exports.eastNorthUpToWorld = eastNorthUpToWorld;
var EPSILON14 = 0.00000000000001;
function equals0(x, eps) {
    return Math.abs(x) <= eps;
}
